'use client';

import { motion } from 'motion/react';
import Image from '@/components/Image';
import { FaChrome } from 'react-icons/fa6';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';

/* ── Mini math problem visuals ── */
function MathVisual({ type }: { type: 'fraction' | 'smartscore' | 'equation' | 'wordproblem' }) {
  if (type === 'fraction') {
    return (
      <div className="flex flex-col items-center gap-1.5">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-10 items-center justify-center rounded-lg bg-[#00B050]/15 text-[11px] font-bold text-[#00B050]">3/4</div>
          <span className="text-[11px] text-[#8A96A8]">+</span>
          <div className="flex h-8 w-10 items-center justify-center rounded-lg bg-[#00B050]/15 text-[11px] font-bold text-[#00B050]">1/3</div>
          <span className="text-[11px] text-[#8A96A8]">=</span>
          <div className="flex h-8 w-10 items-center justify-center rounded-lg border border-dashed border-[#EF4444]/40 text-[11px] text-[#EF4444]">?</div>
        </div>
        <div className="rounded bg-[#EF4444]/10 px-2 py-0.5 text-[9px] text-[#EF4444]">need common denominator</div>
      </div>
    );
  }
  if (type === 'smartscore') {
    return (
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-[#8A96A8]">SmartScore</span>
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-[#E3E8F2]">
            <div className="h-full w-[65%] rounded-full bg-gradient-to-r from-[#FFBF00] to-[#FF8C1A]" />
          </div>
          <span className="text-[11px] font-bold text-[#FF8C1A]">65</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="h-1.5 w-1.5 rounded-full bg-[#EF4444]" />
          <span className="text-[9px] text-[#EF4444]">3 wrong in a row — score dropping</span>
        </div>
      </div>
    );
  }
  if (type === 'equation') {
    return (
      <div className="flex flex-col gap-1.5">
        <div className="rounded bg-[#1865F2]/10 px-3 py-1.5 text-center">
          <span className="text-[12px] font-bold text-[#1865F2]">3x + 7 = 22</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[9px] text-[#8A96A8]">Hint:</span>
          <span className="text-[9px] italic text-[#1865F2]">&ldquo;subtract 7 from both sides&rdquo;</span>
        </div>
        <div className="rounded bg-[#FFBF00]/10 px-2 py-0.5 text-center text-[9px] text-[#CC9900]">
          still confused after reading hint
        </div>
      </div>
    );
  }
  // word problem
  return (
    <div className="flex flex-col gap-1">
      <div className="space-y-0.5 rounded bg-[#F4F7FB] p-2">
        <div className="h-1.5 w-full rounded-full bg-[#E3E8F2]" />
        <div className="h-1.5 w-4/5 rounded-full bg-[#E3E8F2]" />
        <div className="h-1.5 w-3/5 rounded-full bg-[#E3E8F2]" />
      </div>
      <div className="rounded bg-[#EF4444]/10 px-2 py-0.5 text-center text-[9px] text-[#EF4444]">
        can&apos;t figure out what to calculate
      </div>
    </div>
  );
}

/* ── Pain points ── */
const PAIN_POINTS: {
  problem: string;
  context: string;
  pengiResponse: string;
  mathType: 'fraction' | 'smartscore' | 'equation' | 'wordproblem';
}[] = [
  {
    problem: '"I keep getting fractions wrong!"',
    context: 'Doesn\'t find the common denominator, or simplifies incorrectly.',
    pengiResponse: '"To add 3/4 and 1/3, you need the same bottom number. What number works for both 4 and 3?"',
    mathType: 'fraction',
  },
  {
    problem: '"My SmartScore keeps dropping!"',
    context: 'Wrong answers in a row on IXL tank the score. Frustration spirals.',
    pengiResponse: '"Let\'s slow down. Before answering, tell me: what is this problem asking you to find?"',
    mathType: 'smartscore',
  },
  {
    problem: '"Khan Academy hints don\'t help"',
    context: 'Read the hint but still doesn\'t understand the underlying concept.',
    pengiResponse: '"The hint says subtract 7. But why? What are we trying to get alone on one side?"',
    mathType: 'equation',
  },
  {
    problem: '"I don\'t even understand the question"',
    context: 'Word problems that require extracting the math from a story context.',
    pengiResponse: '"Let\'s read it together. What numbers are given? And what is the question asking you to find?"',
    mathType: 'wordproblem',
  },
];

/* ── Onboarding ── */
const ONBOARDING_STEPS = [
  {
    step: '1',
    title: 'Install & sign in',
    subtitle: '30 seconds.',
    body: 'Add the Pengi extension from the Chrome Web Store and sign in with Google or email.',
    detail: 'Works on Chrome, Edge, Brave. Parents can create managed student accounts.',
  },
  {
    step: '2',
    title: 'Open IXL or Khan Academy',
    subtitle: 'Pengi appears automatically.',
    body: 'Navigate to any exercise. Pengi reads the problem and detects the subject — no copy-pasting needed.',
    detail: 'Works on IXL exercises, Khan Academy quizzes, course challenges, and more.',
  },
  {
    step: '3',
    title: 'Ask when stuck',
    subtitle: 'Voice or text.',
    body: '"Why is my answer wrong?" "How do I start this?" "What does this mean?" — Pengi guides you step by step.',
    detail: 'Socratic method: never gives answers, always builds understanding.',
  },
];

/* ── Synergy ── */
const SYNERGY_POINTS = [
  {
    title: 'Homework time',
    description: 'IXL and Khan assignments at 8pm. Parents can\'t help with 5th grade fractions. Pengi can — and it actually teaches, not just gives answers.',
    icon: '📚',
  },
  {
    title: 'Test prep',
    description: 'Practicing for a math test but keeps hitting the same wrong answer? Pengi identifies the gap and asks targeted questions to fill it.',
    icon: '📝',
  },
  {
    title: 'Confidence building',
    description: 'Every "I figured it out!" moment builds math confidence. Pengi creates these moments consistently — not by being easy, but by being patient.',
    icon: '💪',
  },
];

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 },
};

const staggerContainer = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.1 },
  },
};

interface IxlPageClientProps {
  chromeStoreUrl: string;
}

export default function IxlPageClient({ chromeStoreUrl }: IxlPageClientProps) {
  return (
    <>
      {/* ══════ PAIN POINTS ══════ */}
      <section className="py-14 md:py-20" style={{ background: 'linear-gradient(180deg, #FAFCFF 0%, #F4F7FB 30%, #F4F7FB 70%, #FAFCFF 100%)' }}>
        <div className="mx-auto max-w-[1000px] px-4 md:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={fadeInUp} transition={{ duration: 0.5 }}>
            <div className="mb-4 flex items-center gap-3">
              <span className="display-text-semi text-xs uppercase tracking-[0.18em] text-[#8A96A8]">Real Problems</span>
              <div className="h-px flex-1 bg-[#E3E8F2]" />
            </div>
            <div className="mb-12 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <h2 className="emphasize-text max-w-[480px] text-[28px] leading-[1.1] tracking-[-0.03em] text-[#0D1929] md:text-[40px]">Sound familiar?</h2>
              <p className="display-text max-w-[320px] text-sm leading-[1.8] text-[#4A5A72] md:text-right">
                Wrong answer, frustration, give up. Pengi breaks this cycle.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.5 }}
          >
            <Swiper
              modules={[Pagination, Autoplay]}
              spaceBetween={20}
              breakpoints={{
                0: { slidesPerView: 1.08 },
                640: { slidesPerView: 1.5 },
                900: { slidesPerView: 2 },
              }}
              pagination={{ clickable: true }}
              autoplay={{ delay: 3800, disableOnInteraction: false, pauseOnMouseEnter: true }}
              loop
              style={
                {
                  '--swiper-pagination-color': '#1865F2',
                  '--swiper-pagination-bullet-inactive-color': '#D1D9E6',
                  '--swiper-pagination-bullet-inactive-opacity': '1',
                  paddingBottom: '44px',
                } as React.CSSProperties
              }
            >
              {PAIN_POINTS.map((item) => (
                <SwiperSlide key={item.problem}>
                  <div className="group h-full rounded-[20px] border border-[#E3E8F2] bg-white p-6 transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_8px_32px_rgba(0,0,0,0.06)]">
                    <div className="flex gap-4">
                      <div className="hidden shrink-0 sm:block">
                        <div className="w-[140px] overflow-hidden rounded-xl bg-[#F8F9FC] p-3">
                          <MathVisual type={item.mathType} />
                        </div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="emphasize-text text-base text-[#0D1929]">{item.problem}</p>
                        <p className="display-text mt-1 text-sm leading-[1.6] text-[#8A96A8]">{item.context}</p>
                        <div className="mt-3 rounded-xl bg-[#F0FBE8] p-3 transition-all duration-200 group-hover:bg-[#E5F7DA]">
                          <div className="mb-1 flex items-center gap-1.5">
                            <Image src="/assets/landing-logo.png" alt="Pengi" width={16} height={16} optimizedWidth={32} />
                            <span className="display-text-semi text-[11px] text-[#277A1C]">Pengi:</span>
                          </div>
                          <p className="display-text text-[13px] leading-[1.6] text-[#277A1C] italic">{item.pengiResponse}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </motion.div>
        </div>
      </section>

      {/* ══════ STATS + BEFORE/AFTER ══════ */}
      <section className="bg-[#FAFCFF] py-14 md:py-20">
        <div className="mx-auto max-w-[1000px] px-4 md:px-8">
          <motion.div className="mb-16 grid gap-5 sm:grid-cols-3" initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-60px' }} variants={staggerContainer}>
            {[
              { number: '2.4x', label: 'faster skill mastery', sub: 'on IXL and Khan Academy' },
              { number: '73%', label: 'fewer wrong-answer spirals', sub: 'Pengi catches mistakes early' },
              { number: '40 min', label: 'saved per homework session', sub: 'no more Googling for help' },
            ].map((stat) => (
              <motion.div key={stat.label} variants={fadeInUp} transition={{ duration: 0.4 }} className="rounded-[20px] border border-[#E3E8F2] bg-white p-6 text-center transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_28px_rgba(0,0,0,0.04)]">
                <p className="emphasize-text text-[36px] tracking-[-0.03em] text-[#1865F2] md:text-[44px]">{stat.number}</p>
                <p className="emphasize-text mt-1 text-sm text-[#0D1929]">{stat.label}</p>
                <p className="display-text mt-1 text-xs text-[#8A96A8]">{stat.sub}</p>
              </motion.div>
            ))}
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-60px' }} variants={fadeInUp} transition={{ duration: 0.5 }}>
            <div className="mb-4 flex items-center gap-3">
              <span className="display-text-semi text-xs uppercase tracking-[0.18em] text-[#8A96A8]">The Difference</span>
              <div className="h-px flex-1 bg-[#E3E8F2]" />
            </div>
            <h2 className="emphasize-text mb-10 max-w-[500px] text-[28px] leading-[1.1] tracking-[-0.03em] text-[#0D1929] md:text-[36px]">Without Pengi vs. with Pengi</h2>
          </motion.div>

          <motion.div className="grid gap-5 md:grid-cols-2" initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-40px' }} variants={staggerContainer}>
            <motion.div variants={fadeInUp} transition={{ duration: 0.4 }} className="rounded-[20px] border border-[#E3E8F2] bg-white p-7">
              <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-[#FEE2E2] px-3 py-1">
                <div className="h-2 w-2 rounded-full bg-[#EF4444]" />
                <span className="display-text-semi text-xs text-[#B91C1C]">Without Pengi</span>
              </div>
              <div className="space-y-4">
                {[
                  { label: 'Gets problem wrong', bar: 80, time: 'Reads explanation, still confused' },
                  { label: 'Tries again', bar: 75, time: 'Same mistake, SmartScore drops' },
                  { label: 'Asks parent', bar: 60, time: '"I don\'t remember this math"' },
                  { label: 'Gives up', bar: 90, time: 'Closes IXL, dreads next session' },
                ].map((row) => (
                  <div key={row.label}>
                    <div className="flex items-baseline justify-between"><span className="display-text-semi text-[13px] text-[#0D1929]">{row.label}</span><span className="display-text text-[11px] text-[#8A96A8]">{row.time}</span></div>
                    <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-[#F4F7FB]"><div className="h-full rounded-full bg-gradient-to-r from-[#FCA5A5] to-[#EF4444]" style={{ width: `${row.bar}%` }} /></div>
                  </div>
                ))}
              </div>
              <div className="mt-6 rounded-xl bg-[#FEF2F2] p-4 text-center"><p className="emphasize-text text-[13px] text-[#B91C1C]">Result: math anxiety gets worse</p></div>
            </motion.div>

            <motion.div variants={fadeInUp} transition={{ duration: 0.4 }} className="rounded-[20px] border border-[#C5EDB8] bg-white p-7 shadow-[0_4px_24px_rgba(66,197,45,0.06)]">
              <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-[#F0FBE8] px-3 py-1">
                <div className="h-2 w-2 rounded-full bg-[#42C52D]" />
                <span className="display-text-semi text-xs text-[#277A1C]">With Pengi</span>
              </div>
              <div className="space-y-4">
                {[
                  { label: 'Gets problem wrong', bar: 30, time: 'Clicks Pengi: "help me understand"' },
                  { label: 'Guided to the concept', bar: 85, time: '"What does the denominator mean?"' },
                  { label: 'Tries again with understanding', bar: 90, time: 'Gets it right this time' },
                  { label: 'Next problem', bar: 95, time: 'Applies the same thinking' },
                ].map((row) => (
                  <div key={row.label}>
                    <div className="flex items-baseline justify-between"><span className="display-text-semi text-[13px] text-[#0D1929]">{row.label}</span><span className="display-text text-[11px] text-[#8A96A8]">{row.time}</span></div>
                    <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-[#F4F7FB]"><div className="h-full rounded-full bg-gradient-to-r from-[#86EFAC] to-[#42C52D]" style={{ width: `${row.bar}%` }} /></div>
                  </div>
                ))}
              </div>
              <div className="mt-6 rounded-xl bg-[#F0FBE8] p-4 text-center"><p className="emphasize-text text-[13px] text-[#277A1C]">Result: math confidence grows</p></div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ══════ ONBOARDING ══════ */}
      <section className="py-14 md:py-20" style={{ background: 'linear-gradient(180deg, #FAFCFF 0%, #F4F7FB 30%, #F4F7FB 70%, #FAFCFF 100%)' }}>
        <div className="mx-auto max-w-[900px] px-4 md:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={fadeInUp} transition={{ duration: 0.5 }}>
            <div className="mb-4 flex items-center gap-3">
              <span className="display-text-semi text-xs uppercase tracking-[0.18em] text-[#8A96A8]">Get Started</span>
              <div className="h-px flex-1 bg-[#E3E8F2]" />
            </div>
            <h2 className="emphasize-text mb-12 max-w-[500px] text-[28px] leading-[1.1] tracking-[-0.03em] text-[#0D1929] md:text-[40px]">
              From install to &ldquo;aha!&rdquo;
              <br />in 30 seconds
            </h2>
          </motion.div>

          <motion.div className="relative" initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-60px' }} variants={staggerContainer}>
            <div className="absolute left-[27px] top-[48px] hidden h-[calc(100%-96px)] w-0.5 md:block" style={{ background: 'linear-gradient(to bottom, #1865F2, #00B050 60%, transparent)' }} />
            <div className="space-y-5">
              {ONBOARDING_STEPS.map((item) => (
                <motion.div key={item.step} variants={fadeInUp} transition={{ duration: 0.4 }} className="flex gap-5 md:gap-6">
                  <div className="relative z-10 flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-xl shadow-md" style={{ background: 'linear-gradient(135deg, #1865F2 0%, #00B050 100%)', boxShadow: '0 4px 16px rgba(24,101,242,0.25)' }}>
                    <span className="font-extrabold text-white">{item.step}</span>
                  </div>
                  <div className="flex-1 rounded-[20px] border border-[#E3E8F2] bg-white p-6 transition-all duration-200 hover:-translate-y-0.5 hover:border-[#1865F2]/20 hover:shadow-[0_8px_28px_rgba(24,101,242,0.06)]">
                    <div className="flex flex-wrap items-baseline gap-x-3">
                      <h3 className="emphasize-text text-base text-[#0D1929]">{item.title}</h3>
                      <span className="display-text-semi text-xs text-[#1865F2]">{item.subtitle}</span>
                    </div>
                    <p className="display-text mt-2 text-sm leading-[1.7] text-[#4A5A72]">{item.body}</p>
                    <p className="display-text mt-2 text-xs text-[#B0B8C4]">{item.detail}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div className="mt-12 text-center" initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.3 }}>
            <a href={chromeStoreUrl} target="_blank" rel="noopener noreferrer" className="display-text-bold group inline-flex items-center gap-2.5 rounded-full px-8 py-3.5 text-base text-white shadow-[0_4px_24px_rgba(24,101,242,0.3)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_32px_rgba(24,101,242,0.4)]" style={{ background: 'linear-gradient(135deg, #1865F2 0%, #00B050 100%)' }}>
              <FaChrome className="h-5 w-5 transition-transform duration-300 group-hover:rotate-180" />
              Get Started
            </a>
          </motion.div>
        </div>
      </section>

      {/* ══════ AI + HOMEWORK SYNERGY ══════ */}
      <section className="bg-[#FAFCFF] py-14 md:py-20">
        <div className="mx-auto max-w-[1000px] px-4 md:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={fadeInUp} transition={{ duration: 0.5 }}>
            <div className="mb-4 flex items-center gap-3">
              <span className="display-text-semi text-xs uppercase tracking-[0.18em] text-[#8A96A8]">Beyond Homework Help</span>
              <div className="h-px flex-1 bg-[#E3E8F2]" />
            </div>
            <div className="mb-12 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <h2 className="emphasize-text max-w-[520px] text-[28px] leading-[1.1] tracking-[-0.03em] text-[#0D1929] md:text-[40px]">
                Not a homework cheat.
                <br />A homework coach.
              </h2>
              <p className="display-text max-w-[280px] text-sm leading-[1.8] text-[#4A5A72] md:text-right">
                Pengi never gives answers. It builds the thinking that makes answers obvious.
              </p>
            </div>
          </motion.div>

          <motion.div className="grid gap-5 md:grid-cols-3" initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-60px' }} variants={staggerContainer}>
            {SYNERGY_POINTS.map((item) => (
              <motion.div key={item.title} variants={fadeInUp} transition={{ duration: 0.4 }} className="group rounded-[20px] border border-[#E3E8F2] bg-white p-7 transition-all duration-200 hover:-translate-y-1 hover:border-[#1865F2]/20 hover:shadow-[0_8px_32px_rgba(24,101,242,0.08)]">
                <div className="mb-4 text-2xl">{item.icon}</div>
                <h3 className="emphasize-text mb-3 text-base text-[#0D1929]">{item.title}</h3>
                <p className="display-text text-sm leading-[1.7] text-[#566070]">{item.description}</p>
              </motion.div>
            ))}
          </motion.div>

          <motion.div className="mt-8 rounded-[20px] p-7 text-center" style={{ background: 'linear-gradient(135deg, #EEF2FF 0%, #F0FBE8 100%)', border: '1px solid #D8E3EF' }} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.2 }}>
            <p className="emphasize-text text-base text-[#0D1929]">
              The goal isn&apos;t finishing the assignment.
            </p>
            <p className="display-text mt-1.5 text-sm text-[#6B7A90]">
              It&apos;s understanding the math so well that the next assignment is easy.
            </p>
          </motion.div>
        </div>
      </section>
    </>
  );
}
